import React from 'react';
import { Ship, Package, FileCheck, Truck, Globe, CheckCircle, Clock, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const ExportLogistics = () => {
  const logisticsSteps = [
    {
      icon: Package,
      title: 'Product Sourcing & Inspection',
      description: 'Quality-controlled sourcing from trusted farms with rigorous inspection at multiple stages'
    },
    {
      icon: FileCheck,
      title: 'Documentation & Compliance',
      description: 'Complete handling of phytosanitary certificates, certificates of origin, and all export paperwork'
    },
    {
      icon: Truck,
      title: 'Packaging & Loading',
      description: 'Professional packaging to international standards and careful container loading with temperature control where required'
    },
    {
      icon: Ship,
      title: 'International Shipping',
      description: 'Partnerships with trusted global shipping lines for reliable ocean freight services'
    },
    {
      icon: Globe,
      title: 'Customs Clearance',
      description: 'Smooth customs processing at destination ports with complete documentation support'
    },
    {
      icon: CheckCircle,
      title: 'Delivery Confirmation',
      description: 'Final delivery to your warehouse or distribution center with quality verification'
    }
  ];

  const shippingFeatures = [
    {
      icon: Shield,
      title: 'Temperature Control',
      description: 'Reefer containers for products requiring cold chain management (cassava leaves, etc.)',
      highlight: true
    },
    {
      icon: Clock,
      title: 'Reliable Timelines',
      description: 'Consistent shipping schedules with average transit time of 21-28 days to UK ports',
      highlight: false
    },
    {
      icon: FileCheck,
      title: 'Full Documentation',
      description: 'Bill of Lading, Commercial Invoice, Packing List, Certificates included',
      highlight: false
    },
    {
      icon: Truck,
      title: 'Container Options',
      description: '20ft and 40ft containers available. Reefer or dry containers based on product',
      highlight: true
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1606964212858-c215029db704?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3N8ZW58MHx8fHwxNzcxMjgwMTczfDA&ixlib=rb-4.1.0&q=85"
            alt="Export & Logistics"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1B5E20]/80"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Export & Logistics</h1>
          <p className="text-xl text-white/95 max-w-3xl mx-auto">
            Professional supply chain management from farm to your destination
          </p>
        </div>
      </section>

      {/* Logistics Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Our Export Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              End-to-end logistics management ensuring your products arrive fresh and on time
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {logisticsSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="relative border-2 border-gray-200 hover:border-[#C8A951] transition-all hover:shadow-lg bg-white">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-[#C8A951] rounded-full flex items-center justify-center font-bold text-white shadow-lg">
                      {index + 1}
                    </div>
                    <h3 className="text-xl font-semibold text-[#333333] mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Shipping Features */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Shipping Capabilities</h2>
            <p className="text-lg text-gray-600">
              Professional logistics solutions tailored to your product requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {shippingFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card 
                  key={index} 
                  className={`border-0 shadow-lg hover:shadow-xl transition-all ${
                    feature.highlight ? 'bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] text-white' : 'bg-white'
                  }`}
                >
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      feature.highlight ? 'bg-white/20' : 'bg-[#1B5E20]'
                    }`}>
                      <IconComponent className={feature.highlight ? 'text-white' : 'text-white'} size={24} />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold mb-2 ${
                        feature.highlight ? 'text-white' : 'text-[#333333]'
                      }`}>
                        {feature.title}
                      </h3>
                      <p className={feature.highlight ? 'text-white/90' : 'text-gray-600'}>
                        {feature.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Shipping Lines & Destinations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Shipping Lines */}
            <Card className="border-0 shadow-lg bg-[#F9F9F9]">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-lg flex items-center justify-center">
                    <Ship className="text-white" size={24} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#333333]">Trusted Shipping Partners</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  We work with reputable international shipping lines for reliable ocean freight services:
                </p>
                <ul className="space-y-3">
                  {[
                    'Maersk Line',
                    'MSC (Mediterranean Shipping Company)',
                    'CMA CGM',
                    'Hapag-Lloyd',
                    'Other major carriers as per route optimization'
                  ].map((line, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle className="text-[#1B5E20] flex-shrink-0" size={18} />
                      <span className="text-gray-700">{line}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Destinations */}
            <Card className="border-0 shadow-lg bg-[#F9F9F9]">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#C8A951] to-[#B8994A] rounded-lg flex items-center justify-center">
                    <Globe className="text-white" size={24} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#333333]">Primary Destinations</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  We regularly ship to major UK ports and can arrange delivery to other international destinations:
                </p>
                <ul className="space-y-3">
                  {[
                    'Port of Felixstowe (UK)',
                    'Port of Southampton (UK)',
                    'Port of London (UK)',
                    'Port of Liverpool (UK)',
                    'Other European destinations upon request'
                  ].map((destination, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle className="text-[#1B5E20] flex-shrink-0" size={18} />
                      <span className="text-gray-700">{destination}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Documentation */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#333333] mb-4">Export Documentation</h2>
              <p className="text-lg text-gray-600">
                We handle all required paperwork for smooth customs clearance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Phytosanitary Certificate',
                  description: 'Issued by Nigerian Plant Quarantine Service, certifying products are pest and disease-free'
                },
                {
                  title: 'Certificate of Origin',
                  description: 'Official documentation confirming Nigerian origin of products'
                },
                {
                  title: 'Commercial Invoice',
                  description: 'Detailed invoice with product descriptions, quantities, and values'
                },
                {
                  title: 'Packing List',
                  description: 'Complete list of container contents with weights and dimensions'
                },
                {
                  title: 'Bill of Lading',
                  description: 'Shipping document serving as receipt and title of goods'
                },
                {
                  title: 'Quality Inspection Report',
                  description: 'Third-party quality inspection certificate when required'
                }
              ].map((doc, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-all">
                  <FileCheck className="text-[#1B5E20] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-lg font-semibold text-[#333333] mb-2">{doc.title}</h3>
                    <p className="text-gray-600 text-sm">{doc.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Transit Times */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] text-white">
            <CardContent className="p-10">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="text-white" size={32} />
                </div>
                <div>
                  <h3 className="text-3xl font-bold mb-4">Typical Transit Times</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-xl font-semibold mb-2">Nigeria to UK Ports</div>
                      <p className="text-white/90">Average shipping time: 21-28 days (ocean freight)</p>
                    </div>
                    <div>
                      <div className="text-xl font-semibold mb-2">Order Processing</div>
                      <p className="text-white/90">5-7 days for sourcing, quality inspection, and documentation</p>
                    </div>
                    <div className="text-sm text-white/80 mt-6">
                      * Transit times may vary based on shipping line schedules, weather conditions, and port congestion
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ExportLogistics;
